/**
 * One state machine for "fetch something and render it".
 *
 * Every panel had written the same `useState` pair and `useEffect`, and they
 * disagreed on the error type and on whether a not-yet-loaded region rendered
 * as empty, which reads as "there is nothing" rather than "we have not looked".
 *
 * `empty` is therefore a state of its own. `reload` keeps the current data up
 * while it re-reads the same subject; a `deps` change clears it first, because
 * that means a different subject and the old rows would be a lie.
 *
 * @module components/ui/useAsync
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { begin } from "@/utils/begin";

export type AsyncState<T> =
  | { status: "loading" }
  | { status: "error"; error: Error }
  | { status: "empty" }
  | { status: "ready"; data: T };

export interface UseAsyncOptions<T> {
  /** What counts as nothing to show. Without it, a result is never `empty`. */
  isEmpty?: (data: T) => boolean;
  /** Holds the hook at `loading` until its inputs are ready. */
  enabled?: boolean;
}

export type UseAsyncResult<T> = AsyncState<T> & { reload: () => Promise<void> };

function toError(cause: unknown, fallback: string): Error {
  return cause instanceof Error ? cause : new Error(fallback);
}

/**
 * Run `load` when `deps` change and report it as one state.
 *
 * @param load - The read to run. Rejections become the `error` state.
 * @param deps - Re-runs when these change, like any effect dependency list.
 * @param options - See {@link UseAsyncOptions}.
 * @returns The current state, plus a `reload` for after a mutation.
 *
 * @example
 * ```tsx
 * const rounds = useAsync(() => client.listProgrammeReviewRounds({ programmeId }),
 *   [client, programmeId], { isEmpty: (r) => r.length === 0 });
 * ```
 */
export function useAsync<T>(
  load: () => Promise<T>,
  deps: readonly unknown[],
  options: UseAsyncOptions<T> = {},
): UseAsyncResult<T> {
  const { isEmpty, enabled = true } = options;
  const [state, setState] = useState<AsyncState<T>>({ status: "loading" });

  // Held in refs so a caller may pass inline functions without re-running the
  // read on every render; `deps` is what decides when to re-read. Written in an
  // effect rather than during render, and declared before the effect that reads
  // them so they are current by the time it runs.
  const loadRef = useRef(load);
  const isEmptyRef = useRef(isEmpty);
  // A layout effect, so the refs are current before control leaves the commit.
  // React hands a passive effect to its scheduler, which runs it a task later
  // whenever the frame budget has gone, and `reload` starts in a microtask —
  // so a retry pressed in that window read the previous render's closure.
  useLayoutEffect(() => {
    loadRef.current = load;
    isEmptyRef.current = isEmpty;
  });

  // Only the newest run may publish. Without this, a slow first read resolves
  // after a fast second one and overwrites it with stale data.
  const generation = useRef(0);

  const run = useCallback((resetToLoading: boolean): Promise<void> => {
    generation.current += 1;
    // Captured per invocation, not held in a ref: a shared one is read at
    // settle time, by when a later run has already moved it, so a slow first
    // read compares equal to itself and publishes over the newer data.
    const mine = generation.current;
    // State is set only from the continuations, never synchronously here: an
    // update on the effect's own path cascades a render before the first has
    // painted. `begin` is what moves the read off that path.
    return begin(() => {
      // Inside the continuation rather than before it, so the update never
      // lands on the effect's own synchronous path.
      if (resetToLoading) {
        setState({ status: "loading" });
      }
      return loadRef.current();
    }).then(
      (data) => {
        if (generation.current === mine) {
          setState(isEmptyRef.current?.(data) ? { status: "empty" } : { status: "ready", data });
        }
      },
      (cause: unknown) => {
        if (generation.current === mine) {
          setState({ status: "error", error: toError(cause, "Unable to load this section.") });
        }
      },
    );
  }, []);

  useEffect(() => {
    if (!enabled) {
      // Held at `loading`, which is what the option means: the inputs are not
      // ready yet. Publishing `empty` here would have the boundary paint the
      // caller's "there is nothing" copy while the subject is still resolving,
      // telling the reader a programme has no review rounds before anyone has
      // looked.
      return undefined;
    }
    // A deps change means a different subject, so the previous subject's data
    // has to go: leaving it up renders one programme's rows under another's
    // heading. A manual reload re-reads the same subject and keeps its data.
    // `run` settles its own failures into state, so nothing can reject here.
    run(true).catch(() => undefined);
    // Superseding rather than cancelling: the generation counter above already
    // discards a late result, and the platform client exposes no abort.
    return () => {
      generation.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Justification: `deps` is the caller's dependency list by design, exactly as useEffect takes one; listing it as a spread is the only way to forward it, and `run` is stable.
  }, [enabled, run, ...deps]);

  // Held back by `enabled` too: a stale callback or a control that stays live
  // would otherwise run the read with the very inputs the flag exists to wait
  // for, landing the panel in `error` with a platform 400.
  const reload = useCallback(
    (): Promise<void> => (enabled ? run(false) : Promise.resolve()),
    [enabled, run],
  );

  // No render-phase reset here. One was tried: it reset to `loading` the render
  // a serialized `deps` key changed, to stop the commit in between painting the
  // previous subject's rows under the new subject's heading. Giving object deps
  // a per-instance identity then made the key differ on every render for any
  // caller building its dep inline, so the render-phase setState re-rendered
  // and re-keyed for ever until React threw. The one-frame stale paint is the
  // lesser problem, and closing it properly means carrying the key in the
  // state, not adjusting during render.
  return useMemo(() => ({ ...state, reload }), [state, reload]);
}

export interface UseAsyncActionResult<A extends unknown[], R> {
  /** Runs the action. Resolves `undefined` when it failed. */
  run: (...args: A) => Promise<R | undefined>;
  pending: boolean;
  error: Error | null;
  reset: () => void;
}

/**
 * The write-side counterpart: a mutation with its pending flag and its error.
 *
 * @param action - The mutation to run.
 * @returns Its runner, whether it is in flight, and the last failure.
 */
export function useAsyncAction<A extends unknown[], R>(
  action: (...args: A) => Promise<R>,
): UseAsyncActionResult<A, R> {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const actionRef = useRef(action);
  const mounted = useRef(true);
  // The same guard the read side carries. Two overlapping runs otherwise
  // corrupt each other: the second clears the first's error before it has been
  // read, and whichever settles first drops `pending`, re-enabling every
  // control while the other is still in flight.
  const generation = useRef(0);
  const inFlight = useRef(0);
  // A layout effect, for the reason `useAsync` states above. A press that
  // landed in that window ran the previous render's action: the import summary
  // was on the screen and the submit answered that no document had been chosen.
  useLayoutEffect(() => {
    actionRef.current = action;
  });
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const run = useCallback(
    (...args: A): Promise<R | undefined> =>
      begin((): Promise<R | undefined> => {
        generation.current += 1;
        inFlight.current += 1;
        const mine = generation.current;
        setPending(true);
        setError(null);
        const settle = (): void => {
          inFlight.current -= 1;
          // Only when nothing is left running: dropping the flag on the first
          // of two overlapping runs re-enables the UI while the other is still
          // going.
          if (mounted.current && inFlight.current === 0) {
            setPending(false);
          }
        };
        return begin(() => actionRef.current(...args)).then(
          (result): R | undefined => {
            settle();
            return result;
          },
          (cause: unknown): undefined => {
            // Only the newest run may publish a failure; an older one landing
            // afterwards would overwrite the error the user is looking at.
            if (mounted.current && generation.current === mine) {
              setError(toError(cause, "That action could not be completed."));
            }
            settle();
            return undefined;
          },
        );
      }),
    [],
  );

  const reset = useCallback(() => {
    setError(null);
  }, []);

  return { run, pending, error, reset };
}
